import { defineStore } from "pinia";
import useQuasar from 'quasar/src/composables/use-quasar/use-quasar.js';
import { reactive } from "vue";

/**
 * Notify Store
 *
 * This store manages the state and actions related to notifications.
 */
export const useNotifyStore = defineStore("notify", () => {
  // Refs
  const activeNotifications = reactive({});
  const timers = reactive({});

  // Quasar
  const $q = useQuasar();

  /**
   * Sets a notification.
   * @param {Object} params - The parameters for the notification.
   * @param {string} params.key - The key for the notification.
   * @param {string} params.message - The message for the notification.
   * @param {number} [params.duration] - The duration of the notification, -1 for infinite.
   * @param {'success'|'error'} params.type - The type of the notification.
   */
  const setNotification = ({ key, message, type, duration = 5000 }) => {
    if (!key) {
      // Ik snap de onderstaande code niet
      const error = new Error();
      console.error(
        "Error key is required in setErrorMessage is required.",
        error.stack,
      );
      return;
    }

    // If a notification already exists for this key and it's not the same type or message, remove it
    if (
      activeNotifications[key]?.type !== type ||
      activeNotifications[key]?.message !== message
    ) {
      delete activeNotifications[key];
    }

    // If a notification already exists for this key, increment its counter
    if (activeNotifications[key]) {
      activeNotifications[key].counter++;
    } else {
      // Otherwise, add the notification to the activeNotifications with a counter of 1
      activeNotifications[key] = { message, type, counter: 1 };
    }

    // If a timer already exists for this key, clear it
    if (timers[key]) {
      clearTimeout(timers[key]);
    }

    // Set a new timer to remove the notification after 5 seconds
    timers[key] = setTimeout(
      () => {
        delete activeNotifications[key];
        delete timers[key];
      },
      duration === -1 ? 100000000 : duration,
    );
  };

  const setNotifyError = ({
    error = {},
    color = "negative",
    fallbackMessage,
    templateKey = "",
  }) => {
    console.log("🚀 ~ setNotifyError ~ error:", error);
    const data = {
      message: error?.response?.data?.message
        ? error?.response?.data?.message
        : error?.data?.message
          ? error?.data?.message
          : error?.message || fallbackMessage || "Er is iets fout gegaan",
      color,
    };

    if (templateKey.length > 0) {
      setNotification({ key: templateKey, message: data.message, type: color });
    }

    $q.notify(data);
    return {
      ...data,
      key: templateKey,
    };
  };

  const deleteNotification = (key) => {
    delete activeNotifications[key];
    delete timers[key];
  };

  return {
    setNotification,
    deleteNotification,
    activeNotifications,
    setNotifyError,
  };
});
